import {useEffect, useRef} from "react";
import {observer} from "mobx-react";
import {withServices} from "../../react-hooks/with-services";
import {ComponentServicesPick} from "../../services/component-services-pick";
import {GameInfo} from "../../services/games/game-info";
import {JackpotToastWrapperBox} from "./boxes/jackpot-toast-wrapper.box";
import {GameImageContainerComponent} from "../single-game/common/parts/game-image-container.component/game-image-container.component";
import {GameImageContainerBox} from "../single-game/regular-template/boxes/game-image-container/game-image-container.box";
import {JackpotToastCurrencyBox} from "./boxes/time-and-amount/jackpot-toast-currency.box";
import {JackpotToastSuggestedGameBox} from "./boxes/suggested-games/jackpot-toast-suggested-game.box";
import {JackpotToastSuggestedGamesTitleBox} from "./boxes/suggested-games/jackpot-toast-suggested-games-title.box";
import {JackpotToastSuggestedGamesWrapperBox} from "./boxes/suggested-games/jackpot-toast-suggested-games-wrapper.box";
import {JackpotToastLogoAndTitleBox} from "./boxes/logo-and-title/jackpot-toast-logo-and-title.box";
import {JackpotToastLogoBox} from "./boxes/logo-and-title/jackpot-toast-logo.box";
import {AmountAnimator} from "../../../utils/amount-animator/amount-animator";
import {TimeSpan} from "@sparkware/app-utils";
import {NullableDate} from "../../../common/types/nullable-types";
import {JackpotToastTimeAndAmountBox} from "./boxes/time-and-amount/jackpot-toast-time-and-amount.box";
import {JackpotToastClockBox} from "./boxes/time-and-amount/jackpot-toast-clock.box";
import {JackpotToastTitleWrapperBox} from "./boxes/logo-and-title/jackpot-toast-title-wrapper.box";
import {JackpotToastTitleAnimatedBox} from "./boxes/logo-and-title/jackpot-toast-title-animated.box";
import {IToastHandler} from "../../services/toaster/toaster.service.interface";

export enum JackpotToastType {
    Threshold = "threshold",
    Drop = "drop"
}
export const SuggestedGameClickedCloseReason = "SuggestedGameClicked";

export interface JackpotToastComponentProps extends ComponentServicesPick<'theme' | 'jackpots' | 'currency' | 'time' | 'logger' | 'user' | 'analytics'> {
    handler: IToastHandler;
    toastType: JackpotToastType;
    logoImage: string;
    gamesInfo: GameInfo[];
    mainTitle: string;
    secondTitle: string;
    message: string;
    amount: number;
    mustHitTime?: NullableDate;
    onGameClickAnalytics: (gameType: number, gamePosition: number) => void
}

const ComponentBody: React.FC<JackpotToastComponentProps> = observer((props) => {
    let amountAnimatorRef = useRef<AmountAnimator>(null);
    useEffect(() => {
        const amountAnimator = new AmountAnimator(() => props.amount);
        // animate from 3/4 of the final amount but if the difference is bigger than 5000.00 start start from amount - 500000
        const fromAmount = Math.max(Math.round( 3 * props.amount / 4), props.amount - 500000) ;

        amountAnimator.startAnimation({
            animationDuration: TimeSpan.fromSeconds(2.5),
            iterationDuration: TimeSpan.fromMilliseconds(60),
            fromAmount: fromAmount
        });
        amountAnimatorRef.current = amountAnimator;
    }, [props.amount]);

    const getClockValue = () => {

        const mustHitDate = props.mustHitTime;

        if(!mustHitDate) {
            return '';
        }

        const currentDate = props.time.getCurrentDate();
        const seconds = Math.round((mustHitDate.getTime() - currentDate.getTime())/1000);
        const remainingTimeTimeSp = TimeSpan.fromSeconds(Math.max(0, seconds));
        return remainingTimeTimeSp.toString({useTotalHours: true});
    }

    const renderGame = (gameInfo: GameInfo, idx: number) => {
        return <JackpotToastSuggestedGameBox key={gameInfo.gameType}>
            <GameImageContainerComponent gameInfo={gameInfo}
                box={GameImageContainerBox}
                onOpenRealGame={() => {
                    props.onGameClickAnalytics(gameInfo.gameType, idx);
                    gameInfo.open({
                        isRealMode: true
                    });
                    props.handler.close(SuggestedGameClickedCloseReason);
                }}
                useSquareIcons={props.theme.themeCustomizer.useSquareIconsInJackpotToast()}
                playRealGameButton={null}
                disableAnimation={true}
            />
        </JackpotToastSuggestedGameBox>
    }

    const renderMustHitTime = () => {
        if(!props.mustHitTime) {
            return null;
        }
        return <JackpotToastClockBox>
            {getClockValue()}
        </JackpotToastClockBox>
    }

    const renderMoney = () => {
        let formattedMoney = props.currency.formatMoney(props.mustHitTime ? amountAnimatorRef.current?.currentValue : props.amount);
        const decimalSymbol = props.currency.getDecimalSymbol();
        const splittedMoney = formattedMoney.split(decimalSymbol);
        
        if (splittedMoney.length === 2) {
            return <>
                <span>{splittedMoney[0]}</span>
                <span>{decimalSymbol}</span>
                <span className="decimals">{splittedMoney[1]}</span>
            </>;
        } else {
            return formattedMoney;
        }
    }

    return <JackpotToastWrapperBox>
        <JackpotToastLogoAndTitleBox>
            <div style={{textAlign: "center"}}>
                <JackpotToastLogoBox src={props.logoImage} alt="jackpot-logo" />
            </div>
            <JackpotToastTitleWrapperBox>
                <JackpotToastTitleAnimatedBox>{props.mainTitle}</JackpotToastTitleAnimatedBox>
                <JackpotToastTitleAnimatedBox>{props.secondTitle}</JackpotToastTitleAnimatedBox>
            </JackpotToastTitleWrapperBox>
        </JackpotToastLogoAndTitleBox>
        <JackpotToastTimeAndAmountBox>
            {renderMustHitTime()}
            <JackpotToastCurrencyBox>
                {renderMoney()}
            </JackpotToastCurrencyBox>
        </JackpotToastTimeAndAmountBox>
        <JackpotToastSuggestedGamesTitleBox style={{textAlign: "center"}}>{props.message}</JackpotToastSuggestedGamesTitleBox>
        <JackpotToastSuggestedGamesWrapperBox>
            {props.gamesInfo.map(renderGame)}
        </JackpotToastSuggestedGamesWrapperBox>
    </JackpotToastWrapperBox>
});

export const JackpotToastComponent = withServices(ComponentBody, ['theme', 'jackpots', 'currency', 'time', 'logger', 'user', 'analytics']);